<template>
  <div class="dashboard-home-view">
    <v-tabs
      v-model="activeTab"
      class="dashboard-home-tabs"
      dark
      background-color="transparent"
      color="primary"
      :grow="$vuetify.breakpoint.xsOnly"
      show-arrows>
      <v-tab>
        <v-icon left size="19">mdi-view-dashboard-outline</v-icon>
        Übersicht
      </v-tab>
      <v-tab>
        <v-icon left size="19">mdi-download-outline</v-icon>
        Downloads
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="dashboard-home-panels" dark touchless>
      <v-tab-item>
        <div class="dashboard-overview">
          <marketing-tool-header
            eyebrow="Dashboard"
            title="Schnellzugriffe"
            description="Deine wichtigsten Bereiche auf einen Klick."
            icon="mdi-view-grid-plus-outline"
            :logo-src="studioLogoUrl"
            :logo-alt="studioLogoAlt"
            compact-on-mobile>
            <template v-slot:actions>
              <v-btn
                class="primary-action"
                color="#009640"
                depressed
                aria-label="Schnellzugriffe bearbeiten"
                title="Schnellzugriffe bearbeiten"
                :disabled="isLoading || !quickLinkOptions.length"
                @click="openQuickLinkEditor">
                <v-icon left>mdi-tune-variant</v-icon>
                <span class="primary-action__label">Bearbeiten</span>
              </v-btn>
            </template>
          </marketing-tool-header>

          <div class="dashboard-overview-notes">
            <aside
              class="dashboard-motivation"
              :class="{ 'dashboard-motivation--refreshable': canRefreshMotivation }"
              aria-label="Nachricht vom AI Studio-Coach">
              <span class="dashboard-motivation__icon" aria-hidden="true">
                <v-icon size="22">mdi-robot-outline</v-icon>
                <span class="dashboard-motivation__spark">
                  <v-icon size="12">mdi-creation</v-icon>
                </span>
              </span>
              <span class="dashboard-motivation__copy">
                <strong aria-live="polite">{{ dashboardMotivation }}</strong>
              </span>
              <v-btn
                v-if="canRefreshMotivation"
                class="dashboard-motivation__refresh"
                icon
                :loading="refreshingMotivation"
                :disabled="refreshingMotivation"
                aria-label="Neue Nachricht vom AI Studio-Coach generieren"
                title="Neue Nachricht vom AI Studio-Coach generieren"
                @click="refreshDashboardMotivation">
                <v-icon size="20">mdi-refresh</v-icon>
              </v-btn>
            </aside>
          </div>

          <v-card dark flat class="quick-link-surface" aria-labelledby="dashboard-quick-links-title">
            <div class="surface-header">
              <h2 id="dashboard-quick-links-title">Deine Auswahl</h2>
              <span class="count-pill">{{ selectedQuickLinks.length }} {{ selectedQuickLinks.length === 1 ? "Zugriff" : "Zugriffe" }}</span>
            </div>

            <div v-if="isLoading" class="dashboard-quick-links__grid" aria-live="polite" aria-label="Schnellzugriffe werden geladen">
              <div v-for="slotIndex in 3" :key="slotIndex" class="quick-link-slot quick-link-slot--loading" aria-hidden="true">
                <span class="quick-link-slot__skeleton-icon"></span>
                <span class="quick-link-slot__skeleton-line"></span>
              </div>
            </div>

            <div v-else-if="loadError" class="dashboard-quick-links__status" role="alert">
              <span class="dashboard-quick-links__status-icon" aria-hidden="true">
                <v-icon size="26">mdi-cloud-alert-outline</v-icon>
              </span>
              <div>
                <h3>Nicht geladen</h3>
                <p>Bitte versuche es erneut.</p>
              </div>
              <v-btn text large @click="fetchQuickLinks">
                <v-icon left size="19">mdi-refresh</v-icon>
                Erneut laden
              </v-btn>
            </div>

            <div v-else-if="quickLinkOptions.length" class="dashboard-quick-links__grid">
              <template v-for="(item, itemIndex) in quickLinkDisplayItems">
                <button
                  v-if="item"
                  :key="item.id"
                  type="button"
                  class="quick-link-slot quick-link-slot--filled"
                  :aria-label="`${item.text} öffnen`"
                  @click="openQuickLink(item)">
                  <span class="quick-link-slot__topline">
                    <span class="channel-icon" aria-hidden="true">
                      <v-icon size="24">{{ item.icon }}</v-icon>
                    </span>
                  </span>
                  <h3>{{ item.text }}</h3>
                  <v-icon class="quick-link-slot__arrow" size="20" aria-hidden="true">mdi-arrow-right</v-icon>
                </button>

                <button
                  v-else
                  :key="`add-${itemIndex}`"
                  type="button"
                  class="quick-link-slot quick-link-slot--empty"
                  aria-label="Schnellzugriff hinzufügen"
                  @click="openQuickLinkEditor">
                  <span class="quick-link-slot__empty-icon" aria-hidden="true">
                    <v-icon size="24">mdi-plus</v-icon>
                  </span>
                  <h3>Hinzufügen</h3>
                </button>
              </template>
            </div>

            <div v-else class="dashboard-quick-links__status" role="status">
              <span class="dashboard-quick-links__status-icon" aria-hidden="true">
                <v-icon size="26">mdi-shield-lock-outline</v-icon>
              </span>
              <div>
                <h3>Keine Bereiche verfügbar</h3>
              </div>
            </div>
          </v-card>

          <button v-if="canViewTodos" type="button" class="todo-hint" @click="openTodos">
            <span class="todo-hint__icon" aria-hidden="true">
              <v-icon size="21">mdi-format-list-checks</v-icon>
            </span>
            <span class="todo-hint__copy">
              <strong>ToDos</strong>
              <small>Neu unter Schnellzugriffe in der Seitenleiste</small>
            </span>
            <v-icon class="todo-hint__arrow" size="20" aria-hidden="true">mdi-arrow-right</v-icon>
          </button>
        </div>
      </v-tab-item>

      <v-tab-item>
        <dashboard-downloads :studio-logo-url="studioLogoUrl" :studio-logo-alt="studioLogoAlt"></dashboard-downloads>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="quickLinkEditorOpen" persistent scrollable max-width="720" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="quick-link-editor" dark>
        <header class="quick-link-editor__header">
          <span class="quick-link-editor__header-icon" aria-hidden="true">
            <v-icon size="24">mdi-view-grid-plus-outline</v-icon>
          </span>
          <div>
            <h2>Schnellzugriffe bearbeiten</h2>
          </div>
          <v-btn icon :disabled="savingQuickLinks" aria-label="Abbrechen" title="Abbrechen" @click="closeQuickLinkEditor">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </header>

        <v-card-text class="quick-link-editor__content">
          <v-autocomplete
            ref="quickLinkSearch"
            :value="quickLinkCandidate"
            :items="addableQuickLinkOptions"
            class="quick-link-editor__add"
            item-text="searchText"
            item-value="id"
            label="Schnellzugriff hinzufügen"
            prepend-inner-icon="mdi-magnify"
            :disabled="!addableQuickLinkOptions.length"
            outlined
            clearable
            no-data-text="Kein passender Bereich"
            @input="addQuickLink">
            <template v-slot:item="{ item }">
              <v-list-item-icon class="quick-link-editor__option-icon">
                <v-icon size="20">{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="quick-link-editor__option-title">{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle class="quick-link-editor__option-subtitle">{{ item.parentText }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <div class="quick-link-editor__selection-heading">
            <strong>Ausgewählt</strong>
            <span>{{ quickLinkDraft.length }} ausgewählt</span>
          </div>

          <div v-if="editorSelection.length" class="quick-link-editor__selection-list">
            <article v-for="(item, index) in editorSelection" :key="item.id" class="quick-link-editor__selection-row">
              <span class="quick-link-editor__selection-icon" aria-hidden="true">
                <v-icon size="21">{{ item.icon }}</v-icon>
              </span>
              <span class="quick-link-editor__selection-copy">
                <strong>{{ item.text }}</strong>
                <small>{{ item.parentText }}</small>
              </span>
              <span class="quick-link-editor__selection-actions">
                <v-btn icon small :disabled="index === 0" :aria-label="`${item.text} nach vorne verschieben`" @click="moveQuickLink(index, -1)">
                  <v-icon size="19">mdi-arrow-up</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  :disabled="index === editorSelection.length - 1"
                  :aria-label="`${item.text} nach hinten verschieben`"
                  @click="moveQuickLink(index, 1)">
                  <v-icon size="19">mdi-arrow-down</v-icon>
                </v-btn>
                <v-btn icon small :aria-label="`${item.text} entfernen`" @click="removeQuickLink(index)">
                  <v-icon size="19">mdi-delete-outline</v-icon>
                </v-btn>
              </span>
            </article>
          </div>

          <button v-else type="button" class="quick-link-editor__empty" @click="$refs.quickLinkSearch && $refs.quickLinkSearch.focus()">
            <v-icon size="23" aria-hidden="true">mdi-plus-circle-outline</v-icon>
            <span>Noch keine Auswahl</span>
          </button>
        </v-card-text>

        <v-card-actions class="quick-link-editor__actions">
          <v-btn text large :disabled="savingQuickLinks" @click="clearQuickLinks">
            <v-icon left size="19">mdi-close-circle-outline</v-icon>
            Leeren
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text large :disabled="savingQuickLinks" @click="closeQuickLinkEditor">Abbrechen</v-btn>
          <v-btn color="#009640" depressed large :loading="savingQuickLinks" :disabled="!hasDraftChanges" @click="saveQuickLinks">
            <v-icon left size="19">mdi-content-save-outline</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
import { mapState } from "vuex"
import userService from "../../../services/userService"
import MarketingToolHeader from "../communication/MarketingToolHeader.vue"
import dashboardDownloads from "./DashboardDownloads.vue"

const FALLBACK_MOTIVATIONS = [
  "Erst der Plan, dann der Pump.",
  "Kaffee geladen. Studio-Modus aktiviert.",
  "Auch ToDos brauchen manchmal einen Spotter.",
  "Kleine Schritte bringen starke Resultate.",
  "Heute trainieren wir Muskeln und Prozesse.",
  "Ein sauberer Ablauf ist echtes Core-Training.",
  "Motivation startet. Routine übernimmt.",
  "Mehr Fokus, weniger Chaos, bessere Gains.",
  "Stark im Team, stark im Studio.",
  "Ein Klick nach dem anderen, eine Wiederholung nach der anderen.",
  "Das beste Kopf-Workout: ein erledigtes ToDo.",
  "Heute wird organisiert, optimiert und vielleicht trainiert.",
  "Dein Future Self feiert jedes erledigte ToDo.",
  "Gute Energie rein, starke Ergebnisse raus.",
  "Der schwerste Satz ist immer der erste.",
  "Starke Prozesse haben keinen Muskelkater.",
  "Ordnung im Dashboard, Power auf der Fläche.",
  "Heute zählt: dranbleiben und sauber ausführen.",
  "Der Studioalltag ist auch nur ein Zirkeltraining.",
  "Teamwork ist unser stärkster Muskel.",
  "Burpees lösen keine ToDos. Leider.",
]

const MOTIVATION_RETRY_DELAY_MS = 8000
const MOTIVATION_MAX_RETRIES = 6

function getFallbackMotivation(date = new Date()) {
  const dayNumber = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000)
  const index = ((dayNumber % FALLBACK_MOTIVATIONS.length) + FALLBACK_MOTIVATIONS.length) % FALLBACK_MOTIVATIONS.length
  return FALLBACK_MOTIVATIONS[index]
}

export default {
  name: "DashboardHome",
  components: { dashboardDownloads, MarketingToolHeader },
  props: {
    quickLinkOptions: {
      type: Array,
      default: () => [],
    },
    quickLinkOptionsReady: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0,
      dashboardMotivation: getFallbackMotivation(),
      refreshingMotivation: false,
      motivationRetryTimer: null,
      motivationRetryAttempts: 0,
      motivationDisposed: false,
      quickLinkIds: [],
      quickLinkDraft: [],
      quickLinkCandidate: null,
      quickLinkEditorOpen: false,
      loadingQuickLinks: true,
      savingQuickLinks: false,
      loadError: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
    studioLogoUrl() {
      if (!this.selectedStudio || !this.selectedStudio.code) return ""

      const code = String(this.selectedStudio.code).toUpperCase()
      return `https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${code}_BLACK.png`
    },
    studioLogoAlt() {
      const name = this.selectedStudio && this.selectedStudio.name
      return name ? `${name} Logo` : "Studio Logo"
    },
    canViewTodos() {
      return userService.hasRight("todo_fetchAndEdit")
    },
    canRefreshMotivation() {
      return userService.hasRight("stats_transactions")
    },
    isLoading() {
      return this.loadingQuickLinks || !this.quickLinkOptionsReady
    },
    quickLinkOptionsById() {
      return this.quickLinkOptions.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    selectedQuickLinks() {
      return this.quickLinkIds.reduce((result, id) => {
        const item = this.quickLinkOptionsById[id]
        if (item) result.push(item)
        return result
      }, [])
    },
    quickLinkDisplayItems() {
      const items = [...this.selectedQuickLinks]

      if (items.length < this.quickLinkOptions.length) items.push(null)
      return items
    },
    editorSelection() {
      return this.quickLinkDraft.reduce((result, id) => {
        const item = this.quickLinkOptionsById[id]
        if (item) result.push(item)
        return result
      }, [])
    },
    addableQuickLinkOptions() {
      const selectedIds = new Set(this.quickLinkDraft)
      return this.quickLinkOptions.filter((item) => !selectedIds.has(item.id))
    },
    hasDraftChanges() {
      const savedIds = this.normalizeQuickLinkIds(this.quickLinkIds, true)
      return JSON.stringify(savedIds) !== JSON.stringify(this.quickLinkDraft)
    },
  },
  created() {
    this.fetchQuickLinks()
    this.fetchDashboardMotivation()
  },
  beforeDestroy() {
    this.motivationDisposed = true
    this.clearMotivationRetry()
  },
  methods: {
    normalizeQuickLinkIds(value, onlyAvailable = false) {
      const source = Array.isArray(value) ? value : []
      const availableIds = onlyAvailable ? new Set(this.quickLinkOptions.map((item) => item.id)) : null
      const usedIds = new Set()

      return source.reduce((result, id) => {
        if (typeof id !== "string" || usedIds.has(id)) return result
        if (availableIds && !availableIds.has(id)) return result

        usedIds.add(id)
        result.push(id)
        return result
      }, [])
    },
    async fetchQuickLinks() {
      this.loadingQuickLinks = true
      this.loadError = false

      try {
        const response = await api.fetchDashboardQuickLinks()
        this.quickLinkIds = this.normalizeQuickLinkIds(response && response.quickLinks)
      } catch (error) {
        this.loadError = true
      } finally {
        this.loadingQuickLinks = false
      }
    },
    async fetchDashboardMotivation() {
      try {
        const response = await api.fetchDashboardMotivation()
        if (this.motivationDisposed) return

        this.applyDashboardMotivation(response)

        if (response && response.refreshing && this.motivationRetryAttempts < MOTIVATION_MAX_RETRIES) {
          this.motivationRetryAttempts += 1
          this.scheduleMotivationRetry(response.retryAfterMs)
          return
        }
      } catch (error) {
        // Der lokale Fallback bleibt sichtbar, wenn die optionale AI-Funktion ausfaellt.
      }

      this.clearMotivationRetry()
    },
    async refreshDashboardMotivation() {
      if (!this.canRefreshMotivation || this.refreshingMotivation) return

      this.clearMotivationRetry()
      this.refreshingMotivation = true

      try {
        const response = await api.refreshDashboardMotivation()
        if (this.motivationDisposed) return

        this.applyDashboardMotivation(response)

        if (response && response.refreshing) {
          this.motivationRetryAttempts = 1
          this.scheduleMotivationRetry(response.retryAfterMs)
          if (this.$toast) this.$toast.info("Neuer Studio-Impuls wird im Hintergrund erstellt")
        } else if (response && response.generated && this.$toast) {
          this.$toast.success("Neuer Studio-Impuls erstellt")
        }
      } catch (error) {
        if (this.motivationDisposed) return

        const response = error && error.response && error.response.data
        this.applyDashboardMotivation(response)

        const message = response && response.message ? response.message : "Studio-Impuls konnte nicht erstellt werden"
        if (this.$toast) this.$toast.error(message)
      } finally {
        if (!this.motivationDisposed) this.refreshingMotivation = false
      }
    },
    applyDashboardMotivation(response) {
      const motivation = response && typeof response.motivation === "string" ? response.motivation.trim() : ""
      if (motivation) this.dashboardMotivation = motivation
    },
    scheduleMotivationRetry(value) {
      if (this.motivationRetryTimer) clearTimeout(this.motivationRetryTimer)

      const requestedDelay = Number(value)
      const retryDelay = Number.isFinite(requestedDelay) ? Math.min(Math.max(requestedDelay, 3000), 15000) : MOTIVATION_RETRY_DELAY_MS

      this.motivationRetryTimer = setTimeout(() => {
        this.motivationRetryTimer = null
        if (!this.motivationDisposed) this.fetchDashboardMotivation()
      }, retryDelay)
    },
    clearMotivationRetry() {
      if (this.motivationRetryTimer) clearTimeout(this.motivationRetryTimer)
      this.motivationRetryTimer = null
      this.motivationRetryAttempts = 0
    },
    openQuickLink(item) {
      this.$emit("open-quick-link", item)
    },
    openTodos() {
      this.$router.push("/dashboard/todo")
    },
    openQuickLinkEditor() {
      if (this.isLoading || this.loadError || !this.quickLinkOptions.length) return
      this.quickLinkDraft = this.normalizeQuickLinkIds(this.quickLinkIds, true)
      this.quickLinkCandidate = null
      this.quickLinkEditorOpen = true
    },
    closeQuickLinkEditor() {
      if (this.savingQuickLinks) return
      this.quickLinkEditorOpen = false
      this.quickLinkCandidate = null
      this.quickLinkDraft = []
    },
    addQuickLink(id) {
      this.quickLinkCandidate = null
      if (!id || this.quickLinkDraft.includes(id) || !this.quickLinkOptionsById[id]) return
      this.quickLinkDraft.push(id)
    },
    moveQuickLink(index, offset) {
      const targetIndex = index + offset
      if (targetIndex < 0 || targetIndex >= this.quickLinkDraft.length) return

      const selection = [...this.quickLinkDraft]
      const current = selection[index]
      selection[index] = selection[targetIndex]
      selection[targetIndex] = current
      this.quickLinkDraft = selection
    },
    removeQuickLink(index) {
      this.quickLinkDraft.splice(index, 1)
    },
    clearQuickLinks() {
      this.quickLinkDraft = []
    },
    async saveQuickLinks() {
      if (this.savingQuickLinks || !this.hasDraftChanges) return

      const quickLinks = this.normalizeQuickLinkIds(this.quickLinkDraft, true)
      this.savingQuickLinks = true

      try {
        const response = await api.saveDashboardQuickLinks(quickLinks)
        this.quickLinkIds = this.normalizeQuickLinkIds(response && response.quickLinks)
        this.quickLinkEditorOpen = false
        this.quickLinkDraft = []
        if (this.$toast) this.$toast.success("Schnellzugriffe wurden gespeichert")
      } catch (error) {
        if (this.$toast) this.$toast.error("Schnellzugriffe konnten nicht gespeichert werden")
      } finally {
        this.savingQuickLinks = false
      }
    },
  },
}
</script>

<style scoped>
.dashboard-home-view,
.dashboard-home-panels,
.dashboard-home-view ::v-deep .v-tabs-items,
.dashboard-home-view ::v-deep .v-window__container,
.dashboard-home-view ::v-deep .v-window-item {
  width: 100%;
  background: transparent !important;
}

.dashboard-home-tabs {
  margin-bottom: clamp(1rem, 2vw, 1.5rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.dashboard-home-tabs ::v-deep .v-tab {
  min-height: 3rem;
  padding: 0 1.1rem;
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: 0.01em;
  text-transform: none;
}

.dashboard-overview {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text);
  font-size: 1rem;
}

.primary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0 !important;
  text-transform: none;
}

.dashboard-overview-notes {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.dashboard-motivation {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.625rem;
  min-height: 3.5rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.dashboard-motivation {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-color: rgba(88, 211, 136, 0.28);
  background: radial-gradient(circle at 5% -60%, rgba(88, 211, 136, 0.24), transparent 52%),
    linear-gradient(120deg, rgba(0, 150, 64, 0.09), rgba(255, 255, 255, 0.025) 62%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.045), 0 0.75rem 2rem rgba(0, 150, 64, 0.055);
}

.dashboard-motivation::before {
  position: absolute;
  z-index: -1;
  top: 20%;
  bottom: 20%;
  left: 0;
  width: 0.125rem;
  border-radius: 0 999px 999px 0;
  background: linear-gradient(#82e7a8, #009640);
  box-shadow: 0 0 1rem rgba(88, 211, 136, 0.75);
  content: "";
}

.dashboard-motivation--refreshable {
  grid-template-columns: auto minmax(0, 1fr) auto;
}

.dashboard-motivation__icon {
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  color: #58d388;
  border-radius: 0.75rem;
  background: rgba(0, 150, 64, 0.11);
}

.dashboard-motivation__icon {
  position: relative;
  color: #82e7a8;
  border: 1px solid rgba(88, 211, 136, 0.22);
  background: linear-gradient(145deg, rgba(88, 211, 136, 0.18), rgba(0, 150, 64, 0.08));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 0 1.25rem rgba(0, 150, 64, 0.09);
}

.dashboard-motivation__spark {
  position: absolute;
  top: -0.3125rem;
  right: -0.3125rem;
  display: inline-flex;
  width: 1.125rem;
  height: 1.125rem;
  align-items: center;
  justify-content: center;
  color: #102217;
  border: 2px solid #252526;
  border-radius: 999px;
  background: #82e7a8;
}

.dashboard-motivation__icon .v-icon {
  color: currentColor;
}

.dashboard-motivation__copy {
  min-width: 0;
}

.dashboard-motivation__copy strong {
  color: var(--text);
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.35;
}

.dashboard-motivation__refresh {
  width: 2.5rem !important;
  min-width: 2.5rem !important;
  height: 2.5rem !important;
  margin: -0.0625rem;
  color: var(--muted) !important;
  border: 1px solid rgba(88, 211, 136, 0.17);
  border-radius: 0.75rem !important;
  background: linear-gradient(145deg, rgba(88, 211, 136, 0.075), rgba(255, 255, 255, 0.025));
}

.dashboard-motivation__refresh .v-icon {
  color: currentColor;
}

.quick-link-surface {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.surface-header,
.quick-link-slot__topline {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.875rem;
}

.surface-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.125rem;
  line-height: 1.35;
}

.count-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.dashboard-quick-links__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.quick-link-slot {
  min-width: 0;
  min-height: 4.75rem;
  padding: 0.875rem;
  color: inherit;
  font: inherit;
  text-align: left;
  border-radius: 1rem;
  outline: none;
}

.quick-link-slot--filled {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-rows: auto;
  align-items: center;
  column-gap: 0.75rem;
  row-gap: 0;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.quick-link-slot:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.quick-link-slot__topline {
  grid-column: 1;
  grid-row: 1;
  justify-content: space-between;
  gap: 0.75rem;
}

.channel-icon,
.quick-link-slot__empty-icon,
.dashboard-quick-links__status-icon,
.quick-link-editor__header-icon,
.quick-link-editor__selection-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.channel-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.channel-icon .v-icon,
.quick-link-slot__empty-icon .v-icon,
.dashboard-quick-links__status-icon .v-icon,
.quick-link-editor__selection-icon .v-icon {
  color: currentColor;
}

.quick-link-slot h3 {
  grid-column: 2;
  grid-row: 1;
  margin: 0;
  overflow: hidden;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
}

.quick-link-slot__arrow {
  grid-column: 3;
  grid-row: 1;
  color: #8f9095 !important;
}

.quick-link-slot--empty {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--muted);
  text-align: center;
  border: 1px dashed var(--border);
  background: rgba(255, 255, 255, 0.018);
  cursor: pointer;
  transition: color 180ms ease, border-color 180ms ease, background 180ms ease;
}

.quick-link-slot__empty-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.quick-link-slot--empty h3 {
  margin: 0;
}

.quick-link-slot--loading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: auto;
  align-items: center;
  column-gap: 0.75rem;
  row-gap: 0;
  border: 1px solid var(--border);
  background: var(--surface);
  animation: quick-link-pulse 1.2s ease-in-out infinite alternate;
}

.quick-link-slot__skeleton-icon,
.quick-link-slot__skeleton-line {
  display: block;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.09);
}

.quick-link-slot__skeleton-icon {
  width: 2.5rem;
  height: 2.5rem;
  grid-column: 1;
  grid-row: 1;
  margin: 0;
  border-radius: 0.8125rem;
}

.quick-link-slot__skeleton-line {
  grid-column: 2;
  width: 70%;
  height: 0.9rem;
  margin: 0;
}

.dashboard-quick-links__status {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 8rem;
  padding: 1.2rem;
  border: 1px dashed var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
}

.dashboard-quick-links__status-icon {
  width: 3rem;
  height: 3rem;
  color: #58d388;
  border-radius: 0.8125rem;
  background: rgba(0, 150, 64, 0.11);
}

.dashboard-quick-links__status h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  font-weight: 780;
  line-height: 1.4;
}

.dashboard-quick-links__status p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.dashboard-quick-links__status .v-btn {
  margin-left: auto;
  min-height: 2.75rem;
  font-size: 0.875rem;
  text-transform: none;
}

.todo-hint {
  display: grid;
  width: 100%;
  min-height: 4rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.625rem 0.75rem;
  color: var(--text);
  font: inherit;
  text-align: left;
  border: 1px solid rgba(0, 150, 64, 0.22);
  border-radius: 1rem;
  outline: none;
  background: rgba(0, 150, 64, 0.055);
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease;
}

.todo-hint__icon {
  display: inline-flex;
  width: 2.5rem;
  height: 2.5rem;
  align-items: center;
  justify-content: center;
  color: #58d388;
  border-radius: 0.75rem;
  background: rgba(0, 150, 64, 0.12);
}

.todo-hint__icon .v-icon,
.todo-hint__arrow {
  color: currentColor;
}

.todo-hint__copy {
  display: grid;
  min-width: 0;
}

.todo-hint__copy strong {
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
}

.todo-hint__copy small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.4;
}

.todo-hint__arrow {
  color: #8f9095;
}

.todo-hint:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.quick-link-editor {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  color: var(--text) !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  font-size: 1rem;
}

.quick-link-editor__header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.quick-link-editor__header-icon {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.quick-link-editor__header-icon .v-icon {
  color: currentColor;
}

.quick-link-editor__header > div {
  min-width: 0;
}

.quick-link-editor__header > .v-btn {
  margin-left: auto;
}

.quick-link-editor__header h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.3;
}

.quick-link-editor__content {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.quick-link-editor__add ::v-deep .v-input__slot {
  min-height: 3.5rem;
  border-radius: 0.8125rem !important;
  background: var(--surface) !important;
}

.quick-link-editor__add ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.quick-link-editor__add ::v-deep .v-label,
.quick-link-editor__add ::v-deep input,
.quick-link-editor__add ::v-deep .v-messages__message {
  font-size: 0.875rem;
}

.quick-link-editor__option-icon {
  align-self: center !important;
  margin-right: 0.75rem !important;
}

.quick-link-editor__option-title {
  font-size: 0.875rem !important;
  font-weight: 700;
}

.quick-link-editor__option-subtitle {
  font-size: 0.75rem !important;
}

.quick-link-editor__selection-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.15rem 0 0.65rem;
}

.quick-link-editor__selection-heading strong {
  color: var(--text);
  font-size: 0.875rem;
  font-weight: 800;
}

.quick-link-editor__selection-heading span {
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 750;
}

.quick-link-editor__selection-list {
  display: grid;
  gap: 0.55rem;
}

.quick-link-editor__selection-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  min-height: 4.25rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.8125rem;
  background: var(--surface);
}

.quick-link-editor__selection-icon {
  width: 2.6rem;
  height: 2.6rem;
  color: #58d388;
  border-radius: 0.75rem;
  background: rgba(0, 150, 64, 0.11);
}

.quick-link-editor__selection-copy {
  min-width: 0;
}

.quick-link-editor__selection-copy strong,
.quick-link-editor__selection-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-link-editor__selection-copy strong {
  color: var(--text);
  font-size: 0.875rem;
  font-weight: 750;
}

.quick-link-editor__selection-copy small {
  margin-top: 0.15rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.quick-link-editor__selection-actions {
  display: flex;
  gap: 0.15rem;
}

.quick-link-editor__selection-actions .v-btn {
  width: 2.5rem;
  height: 2.5rem;
}

.quick-link-editor__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  min-height: 5rem;
  padding: 1rem;
  color: var(--muted);
  font: inherit;
  font-size: 0.875rem;
  border: 1px dashed var(--border);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

.quick-link-editor__actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.quick-link-editor__actions .v-btn {
  min-height: 2.75rem;
  margin: 0 !important;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .dashboard-motivation__refresh:hover {
    color: #58d388 !important;
    border-color: rgba(0, 150, 64, 0.32);
    background: rgba(0, 150, 64, 0.09);
  }

  .quick-link-slot--filled:hover {
    border-color: var(--border-strong);
    background: var(--surface-strong);
    transform: translateY(-2px);
  }

  .quick-link-slot--empty:hover {
    color: #ffffff;
    border-color: var(--border-strong);
    background: var(--surface-strong);
  }

  .todo-hint:hover {
    border-color: rgba(0, 150, 64, 0.38);
    background: rgba(0, 150, 64, 0.085);
  }
}

@media (max-width: 78.9375rem) {
  .dashboard-overview {
    padding: 1.125rem;
  }

  .dashboard-quick-links__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .dashboard-overview-notes {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .dashboard-quick-links__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .dashboard-home-tabs {
    width: 100%;
    margin-bottom: 0.625rem;
    padding: 0.25rem;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 0.875rem;
    background: rgba(255, 255, 255, 0.03);
  }

  .dashboard-home-tabs ::v-deep .v-tabs-bar {
    height: 2.625rem;
    background: transparent !important;
  }

  .dashboard-home-tabs ::v-deep .v-tab {
    min-width: 0;
    min-height: 2.25rem;
    padding: 0 0.5rem;
    border-radius: 0.625rem;
  }

  .dashboard-home-tabs ::v-deep .v-tab--active {
    background: rgba(0, 150, 64, 0.14);
  }

  .dashboard-home-tabs ::v-deep .v-tabs-slider-wrapper,
  .dashboard-home-tabs ::v-deep .v-slide-group__prev,
  .dashboard-home-tabs ::v-deep .v-slide-group__next {
    display: none !important;
  }

  .dashboard-overview {
    padding: 0;
  }

  .quick-link-surface {
    margin-top: 0.625rem;
    padding: 0.75rem;
    border-radius: 1rem !important;
  }

  .dashboard-overview-notes {
    margin-top: 0.625rem;
  }

  .dashboard-motivation {
    padding: 0.5rem 0.625rem;
    border-radius: 0.875rem;
  }

  .dashboard-motivation__icon {
    width: 2.25rem;
    height: 2.25rem;
  }

  .surface-header {
    align-items: center;
    flex-direction: row;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .surface-header h2 {
    font-size: 1rem;
  }

  .dashboard-quick-links__grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .quick-link-slot {
    min-height: 4.5rem;
    padding: 0.75rem;
    border-radius: 0.875rem;
  }

  .quick-link-slot--filled {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-rows: auto;
    align-items: center;
    column-gap: 0.75rem;
    row-gap: 0;
  }

  .quick-link-slot__topline {
    grid-column: 1;
    grid-row: 1;
  }

  .channel-icon,
  .quick-link-slot__empty-icon {
    width: 2.375rem;
    height: 2.375rem;
    border-radius: 0.75rem;
  }

  .quick-link-slot h3 {
    grid-column: 2;
    grid-row: 1;
    margin: 0;
    line-height: 1.3;
  }

  .quick-link-slot--empty {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    justify-content: stretch;
    gap: 0.75rem;
    text-align: left;
  }

  .quick-link-slot--empty h3 {
    grid-column: 2;
    margin: 0;
  }

  .quick-link-slot--loading {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-rows: auto;
    align-items: center;
    justify-content: stretch;
    column-gap: 0.75rem;
    row-gap: 0;
  }

  .quick-link-slot__skeleton-icon {
    width: 2.375rem;
    height: 2.375rem;
    grid-column: 1;
    grid-row: 1;
    margin: 0;
  }

  .quick-link-slot__skeleton-line {
    grid-column: 2;
    margin: 0;
  }

  .dashboard-quick-links__status {
    align-items: flex-start;
    flex-wrap: wrap;
    min-height: 6.5rem;
    padding: 0.875rem;
  }

  .dashboard-quick-links__status .v-btn {
    width: 100%;
    margin-left: 0;
  }

  .todo-hint {
    min-height: 3.75rem;
    margin-top: 0.625rem;
    padding: 0.625rem 0.75rem;
    border-radius: 0.875rem;
  }

  .quick-link-editor {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .quick-link-editor__header {
    align-items: center;
    padding: max(0.75rem, env(safe-area-inset-top, 0px)) 0.875rem 0.75rem;
  }

  .quick-link-editor__header-icon {
    width: 2.5rem;
    height: 2.5rem;
  }

  .quick-link-editor__header h2 {
    font-size: 1.125rem;
  }

  .quick-link-editor__content {
    padding: 0.75rem !important;
  }

  .quick-link-editor__selection-row {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .quick-link-editor__selection-actions {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }

  .quick-link-editor__selection-actions .v-btn {
    width: 2.75rem;
    height: 2.75rem;
  }

  .quick-link-editor__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-height: 0;
    padding: 0.75rem 0.875rem max(0.875rem, env(safe-area-inset-bottom, 0px)) !important;
  }

  .quick-link-editor__actions .v-btn,
  .quick-link-editor__actions .v-spacer {
    width: 100%;
  }

  .quick-link-editor__actions .v-spacer {
    display: none;
  }

  .quick-link-editor__actions .v-btn:last-child {
    grid-column: 1 / -1;
    grid-row: 1;
  }
}

@media (max-width: 22.4375rem) {
  .quick-link-surface {
    padding: 0.625rem;
  }

  .dashboard-quick-links__grid {
    gap: 0.375rem;
  }

  .quick-link-slot {
    min-height: 4.25rem;
    padding: 0.625rem;
  }

  .primary-action {
    width: 2.5rem;
    min-width: 2.5rem !important;
    padding: 0 !important;
  }

  .primary-action .v-icon {
    margin: 0 !important;
  }

  .primary-action__label {
    display: none;
  }
}

@keyframes quick-link-pulse {
  from {
    opacity: 0.58;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .quick-link-slot--filled,
  .quick-link-slot--empty,
  .quick-link-slot--loading {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
